import React, { FC, useState } from "react"
import { Dialog, Input, Textarea, Button, IconButton } from "../../ui"
import { Controller, useForm } from "react-hook-form"
import z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Add, Close } from "@mui/icons-material"
import DatePicker from "../../ui/DatePicker"
import dayjs from "dayjs"
import cn from "../../../lib/utils/cn"
import { useShareFile } from "../../../hooks/api/documentHooks"
import { EditingItem } from "../../../pages/Workspace/components/interfaces.table"
import FileFormatIcon from "../../common/FileFormatIcon"

interface ShareFileModalProps {
  isOpen: boolean
  onClose: () => void
  file: EditingItem | null
}

const formSchema = z.object({
  email: z.string().email("Invalid email format").optional().or(z.literal("")),
  users: z.array(z.string().email()),
  message: z.string().optional(),
  dueDate: z.string().min(1, "Due date is required"),
})

type FormValues = z.infer<typeof formSchema>

const ShareFileModal: FC<ShareFileModalProps> = ({ isOpen, onClose, file }) => {

  const { mutateAsync: shareFile, isPending: isSharing } = useShareFile()

  const {
    control,
    watch,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
    handleSubmit,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      users: [],
      message: "",
      dueDate: dayjs().add(1, "week").format("YYYY-MM-DD"),
    },
  })

  const { email, users, message, dueDate } = watch()

  const handleClose = () => {
    onClose()
    reset({
      email: "",
      users: [],
      message: "",
      dueDate: dayjs().add(1, "week").format("YYYY-MM-DD"),
    })
  }

  const handleAddEmail = () => {
    if (email && isValid && !users.includes(email.toLowerCase())) {
      setValue("users", [...users, email.toLowerCase()])
      setValue("email", "")
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setValue(
      "users",
      users.filter((user) => user !== emailToRemove)
    )
  }

  const onSubmit = async (data: FormValues) => {
    await shareFile({
      id: file?.id,
      data: {
        shareMessage: data.message ?? "",
        users: data.users,
        dueDate: data.dueDate,
      }
    }, {
      onSuccess: handleClose
    })

  }

  const isEmailValid = email && z.string().email().safeParse(email).success
  const isEmailDuplicate = email && users.includes(email.toLowerCase())

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      isDirty={isDirty}
      title="Share File"
      controllers={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button
            variant="tertiary"
            label="Cancel"
            onClick={handleClose}
          />
          <Button
            variant="primary"
            label="Share"
            onClick={handleSubmit(onSubmit)}
            disabled={!users.length || !dueDate}
            loading={isSharing}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* File info block */}
        {file && (
          <div className="flex items-center justify-start px-3 h-12 bg-background-light dark:bg-background-light-dark rounded-lg gap-3">
            <FileFormatIcon extension={file.fileType} fileName={file.name} />
            <p className="text-sm text-text-main dark:text-text-main-dark">{file.name}</p>
          </div>
        )}
        {/* Email Input */}
        <Controller
          name="email"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Input
              value={value}
              onChange={onChange}
              label="Email"
              wrapperClassName="h-fit"
              inputClassName="h-12"
              placeholder="Enter user email"
              onEnterClick={handleAddEmail}
              rightIcon={
                <IconButton
                  variant="secondary"
                  icon={
                    <Add className="text-text-main dark:text-text-main-dark" />
                  }
                  className="w-8 h-8 rounded dark:disabled:opacity-40"
                  onClick={handleAddEmail}
                  disabled={!isEmailValid || !!isEmailDuplicate}
                  tooltip={
                    !isEmailValid
                      ? "Invalid email format"
                      : isEmailDuplicate
                        ? "This email is already in the list"
                        : "Add email to list"
                  }
                />
              }
              error={errors?.email?.message}
            />
          )}
        />

        {/* Users List */}
        {!!users?.length && (
          <div className="rounded-lg border border-stroke-light dark:border-stroke-light-dark default-shadow max-h-40 box-border overflow-y-auto">
            {users?.map((user, index) => (
              <div
                key={`${index}-${user}`}
                className={cn(
                  "pl-5 pr-1 h-12 flex items-center justify-between",
                  index % 2 !== 0
                    ? "bg-background-light dark:bg-background-light-dark"
                    : "bg-white dark:bg-background-dark"
                )}
              >
                <p className="text-base text-text-main dark:text-text-main-dark flex items-center justify-start gap-2">
                  {user}
                </p>
                <IconButton
                  variant="transparent"
                  icon={
                    <Close className="w-5 h-5 text-text-main dark:text-text-main-dark" />
                  }
                  onClick={() => handleRemoveEmail(user)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Message Textarea */}
        <Controller
          name="message"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Textarea
              value={value}
              onChange={onChange}
              label="Message"
              wrapperClassName="h-fit"
              textareaClassName="h-24 px-4 text-text-main dark:text-text-main-dark"
              placeholder="Enter message (optional, max 200 ch)"
              maxLength={200}
            />
          )}
        />

        {/* Date Picker */}
        <Controller
          name="dueDate"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-col gap-1">
              <DatePicker
                label="Expires On"
                value={value ? dayjs(value).toDate() : null}
                onChange={(date) =>
                  onChange(date ? dayjs(date).format("YYYY-MM-DD") : "")
                }
                placeholder="Select date"
                classNameLabel="text-sm"
                wrapperClassName="w-auto"
                className="h-12 px-2 "
                classNameCalendarWrapper="w-[105%] -left-4 -mb-7 dark:bg-background-light-dark"
                labelClassName="font-normal"
                calendarIconPosition="right"
                formatTemplate="DD MMM YYYY"
                dropdownPosition="top"
                required
              />
              {errors?.dueDate?.message && (
                <p className="text-sm text-error">
                  {errors.dueDate.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
    </Dialog>
  )
}

export default ShareFileModal